import SEO from "../../components/SEO";
import { BsEnvelope, BsTelephone, BsWhatsapp, BsGeoAlt, BsClock, BsSearch, BsQuestionCircle, BsArrowRight, BsBoxSeam } from "react-icons/bs";

const SUPPORT_PHONE_DISPLAY = "069 904 2846";
const SUPPORT_WHATSAPP_NUMBER = "27699042846";
const SUPPORT_EMAIL = "support@parcelin.co.za";

const directionsUrl = (query) =>
	`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;

function ContactMethod({ href, icon, label, value, external }) {
	return (
		<a
			href={href}
			{...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
			className="group flex items-center gap-4 bg-white rounded-2xl border border-purple-100 p-5 shadow-sm hover:shadow-md hover:border-purple-300 transition-all"
		>
			<span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-purple-50 text-purple-600 text-xl group-hover:bg-purple-600 group-hover:text-white transition-colors">
				{icon}
			</span>
			<span className="min-w-0">
				<span className="block text-sm font-medium text-gray-500">{label}</span>
				<span className="block text-base font-semibold text-gray-900 truncate">{value}</span>
			</span>
			<BsArrowRight className="ml-auto flex-shrink-0 text-gray-300 group-hover:text-purple-600 transition-colors" aria-hidden="true" />
		</a>
	);
}

function OfficeCard({ tag, name, addressLines, mapsQuery }) {
	return (
		<div className="flex flex-col bg-white rounded-2xl border border-purple-100 p-6 shadow-sm">
			<span className="self-start text-xs font-medium uppercase tracking-wide text-purple-700 bg-purple-50 rounded-full px-3 py-1 mb-4">
				{tag}
			</span>
			<div className="flex items-start gap-3 flex-1">
				<BsGeoAlt className="text-purple-600 text-xl flex-shrink-0 mt-1" aria-hidden="true" />
				<div>
					<h3 className="text-xl text-gray-900 mb-2">{name}</h3>
					<address className="not-italic text-gray-600 leading-relaxed">
						{addressLines.map((line) => (
							<span key={line} className="block">{line}</span>
						))}
					</address>
				</div>
			</div>
			<a
				href={directionsUrl(mapsQuery)}
				target="_blank"
				rel="noopener noreferrer"
				className="mt-5 inline-flex items-center gap-2 self-start text-sm font-medium text-purple-700 hover:text-purple-900"
			>
				Get directions <BsArrowRight aria-hidden="true" />
			</a>
		</div>
	);
}

function MessageForm() {
	// There is no contact endpoint on the API, so the message is handed to
	// WhatsApp or the visitor's email app, ready to send to our support team.
	const handleSubmit = (event) => {
		event.preventDefault();
		const form = new FormData(event.currentTarget);
		const channel = event.nativeEvent.submitter?.value ?? "whatsapp";
		const subject = form.get("subject");
		const details = [
			`Hi Parcelin, my name is ${form.get("name").trim()}.`,
			"",
			form.get("message").trim(),
			form.get("phone").trim() ? `\nPhone: ${form.get("phone").trim()}` : "",
		].join("\n");

		if (channel === "email") {
			window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(`Parcelin: ${subject}`)}&body=${encodeURIComponent(details)}`;
		} else {
			window.open(
				`https://wa.me/${SUPPORT_WHATSAPP_NUMBER}?text=${encodeURIComponent(`${subject}\n\n${details}`)}`,
				"_blank",
				"noopener,noreferrer",
			);
		}
	};

	const fieldClass =
		"w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-500 transition-colors";

	return (
		<form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-purple-100 shadow-sm p-6 md:p-8 space-y-4">
			<div>
				<h2 className="text-2xl text-gray-900">Send us a message</h2>
				<p className="text-sm text-gray-500 mt-1">
					Your message opens in WhatsApp or your email app, ready to send.
				</p>
			</div>

			<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
				<div>
					<label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1.5">Full name *</label>
					<input id="name" name="name" type="text" required autoComplete="name" placeholder="Your name" className={fieldClass} />
				</div>
				<div>
					<label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1.5">Phone number</label>
					<input id="phone" name="phone" type="tel" autoComplete="tel" placeholder="069 123 4567" className={fieldClass} />
				</div>
			</div>

			<div>
				<label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1.5">Subject *</label>
				<select id="subject" name="subject" required defaultValue="" className={fieldClass}>
					<option value="" disabled>Select a subject</option>
					<option>General enquiry</option>
					<option>Booking question</option>
					<option>Tracking issue</option>
					<option>Payment question</option>
					<option>Business account</option>
					<option>Become a driver</option>
					<option>Feedback</option>
					<option>Other</option>
				</select>
			</div>

			<div>
				<label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1.5">Message *</label>
				<textarea id="message" name="message" required rows="5" placeholder="Tell us how we can help. Include your tracking number if it's about a parcel." className={fieldClass} />
			</div>

			<div className="flex flex-col sm:flex-row gap-3 pt-1">
				<button type="submit" value="whatsapp" className="flex-1 inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white py-3 rounded-xl font-semibold transition-colors">
					<BsWhatsapp aria-hidden="true" /> Send on WhatsApp
				</button>
				<button type="submit" value="email" className="flex-1 inline-flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 text-white py-3 rounded-xl font-semibold transition-colors">
					<BsEnvelope aria-hidden="true" /> Send by email
				</button>
			</div>
		</form>
	);
}

export const ContactPage = () => {
	return (
		<div className="w-full min-h-screen bg-gradient-to-br from-cyan-100 to-fuchsia-200 py-12 md:py-16 px-4 sm:px-5">
			<SEO
				title="Contact Us"
				description="Get in touch with Parcelin. Call or WhatsApp 069 904 2846, email support@parcelin.co.za, or visit us in Midrand or Giyani."
				path="/contact"
			/>
			<div className="max-w-6xl mx-auto space-y-12">
				<header className="text-center max-w-2xl mx-auto">
					<p className="text-sm font-medium uppercase tracking-widest text-purple-600 mb-3">Contact us</p>
					<h1 className="text-4xl md:text-5xl text-purple-800 mb-4">Get in touch</h1>
					<p className="text-lg text-gray-700">
						Questions about a delivery, a quote or a business account? Reach us whichever way suits you.
					</p>
				</header>

				<section aria-label="Ways to contact us" className="grid grid-cols-1 md:grid-cols-3 gap-4">
					<ContactMethod href="tel:+27699042846" icon={<BsTelephone />} label="Call us" value={SUPPORT_PHONE_DISPLAY} />
					<ContactMethod href={`https://wa.me/${SUPPORT_WHATSAPP_NUMBER}`} external icon={<BsWhatsapp />} label="WhatsApp" value={SUPPORT_PHONE_DISPLAY} />
					<ContactMethod href={`mailto:${SUPPORT_EMAIL}`} icon={<BsEnvelope />} label="Email" value={SUPPORT_EMAIL} />
				</section>

				<section aria-labelledby="offices-heading">
					<h2 id="offices-heading"className="text-2xl md:text-3xl text-purple-800 mb-5">Our offices</h2>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-5">
						<OfficeCard
							tag="Warehouse"
							name="Midrand"
							addressLines={["Gallagher Business Exchange", "Office Park, 688 Gallagher Ave", "Halfway House, Midrand, 1685"]}
							mapsQuery="Gallagher Business Exchange, 688 Gallagher Ave, Halfway House, Midrand, 1685"
						/>
						<OfficeCard
							tag="Office"
							name="Giyani"
							addressLines={["Bafana Hardware Building", "Old FNB Road, Giyani CBD", "Behind SupaQuick"]}
							mapsQuery="Bafana Hardware, Old FNB Road, Giyani"
						/>
					</div>
				</section>

				<div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
					<div className="lg:col-span-2 space-y-6">
						<div className="bg-white rounded-2xl border border-purple-100 shadow-sm p-6">
							<div className="flex items-center gap-3 mb-4">
								<BsClock className="text-purple-600 text-xl" aria-hidden="true" />
								<h2 className="text-xl text-gray-900">Support hours</h2>
							</div>
							<dl className="divide-y divide-gray-100 text-sm">
								{[
									["Monday – Friday", "8:00 AM – 6:00 PM"],
									["Saturday", "9:00 AM – 2:00 PM"],
									["Sunday", "Closed"],
								].map(([day, hours]) => (
									<div key={day} className="flex justify-between py-2.5">
										<dt className="text-gray-600">{day}</dt>
										<dd className="font-semibold text-gray-900">{hours}</dd>
									</div>
								))}
							</dl>
						</div>

						<div className="bg-white rounded-2xl border border-purple-100 shadow-sm p-6">
							<div className="flex items-center gap-3 mb-4">
								<BsBoxSeam className="text-purple-600 text-xl" aria-hidden="true" />
								<h2 className="text-xl text-gray-900">Where we deliver</h2>
							</div>
							<div className="space-y-3 text-sm">
								<div>
									<p className="font-semibold text-gray-900">Limpopo</p>
									<p className="text-gray-600">Polokwane, Giyani, Malamulele, Tzaneen, Bungeni, Thohoyandou, Phalaborwa</p>
								</div>
								<div>
									<p className="font-semibold text-gray-900">Gauteng</p>
									<p className="text-gray-600">Within 70km of our Midrand warehouse</p>
								</div>
							</div>
						</div>
					</div>

					<div className="lg:col-span-3">
						<MessageForm />
					</div>
				</div>

				<section aria-label="Self-help" className="grid grid-cols-1 sm:grid-cols-2 gap-4">
					<a href="/track-parcel" className="group flex items-center gap-4 bg-gradient-to-br from-purple-600 to-fuchsia-600 text-white rounded-2xl p-6 shadow-md hover:shadow-lg transition-shadow">
						<BsSearch className="text-2xl flex-shrink-0" aria-hidden="true" />
						<span>
							<span className="block text-lg font-semibold">Track your parcel</span>
							<span className="block text-sm text-white/80">Check live status with your tracking number</span>
						</span>
						<BsArrowRight className="ml-auto flex-shrink-0 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
					</a>
					<a href="/faq" className="group flex items-center gap-4 bg-white rounded-2xl border border-purple-100 p-6 shadow-sm hover:shadow-md transition-shadow">
						<BsQuestionCircle className="text-2xl text-purple-600 flex-shrink-0" aria-hidden="true" />
						<span>
							<span className="block text-lg font-semibold text-gray-900">Read the FAQ</span>
							<span className="block text-sm text-gray-500">Quick answers to common questions</span>
						</span>
						<BsArrowRight className="ml-auto flex-shrink-0 text-gray-400 group-hover:translate-x-1 group-hover:text-purple-600 transition-all" aria-hidden="true" />
					</a>
				</section>
			</div>
		</div>
	);
};
